/**
 * UK postcode geocoding via postcodes.io (free, no API key, CORS-enabled).
 * Returns {lat, lng, formatted} or null if invalid.
 */
export async function geocodePostcode(postcode) {
  if (!postcode) return null;
  const clean = postcode.replace(/\s+/g, "").toUpperCase();
  try {
    const resp = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(clean)}`);
    if (!resp.ok) return null;
    const data = await resp.json();
    if (data.status !== 200 || !data.result) return null;
    return {
      lat: data.result.latitude,
      lng: data.result.longitude,
      formatted: data.result.postcode,
      admin_district: data.result.admin_district,
      region: data.result.region,
    };
  } catch {
    return null;
  }
}

/**
 * Haversine distance in km between two lat/lng points.
 */
export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth radius in km
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Crude UK driving-time estimate: assumes ~75 km/h average on long routes,
 * 55 km/h on short routes (more urban / A-roads). No real routing — for that
 * we'd need a paid API. This is an honest "rough drive time".
 */
export function estimateDriveMinutes(km) {
  if (km == null) return null;
  const avgSpeed = km > 60 ? 75 : 55; // km/h
  return Math.round((km / avgSpeed) * 60);
}

export function formatKm(km) {
  if (km == null) return "";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

export function formatDriveTime(mins) {
  if (mins == null) return "";
  if (mins < 60) return `~${mins} min drive`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `~${h}h drive` : `~${h}h ${m}m drive`;
}

/** Basic UK postcode validation regex — accepts most real formats. */
export function looksLikePostcode(input) {
  if (!input) return false;
  return /^[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}$/i.test(input.trim());
}
